import { freeze } from 'icepick';
import { createSelector } from 'reselect';
import countBy from 'lodash/countBy';
import difference from 'lodash/difference';
import findLast from 'lodash/findLast';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import mapValues from 'lodash/mapValues';
import max from 'lodash/max';
import memoize from 'lodash/memoize';
import omit from 'lodash/omit';
import some from 'lodash/some';
import sum from 'lodash/sum';
import trim from 'lodash/trim';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { alphabeticalSort } from '@atlassian/jira-common-util-software-filters-sort/src/index.tsx';
import {
	BASE_HIERARCHY_TYPE,
	PARENT_HIERARCHY_TYPE,
	isSubtaskHierarchyLevel,
} from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import { values } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ExtraField } from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import { UNASSIGNED_VERSION_ID } from '@atlassian/jira-software-filters/src/common/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	CARD_ROW_HEIGHT,
	COMPACT_CARD_ROW_HEIGHT,
	EXTRA_CARD_ROW_HEIGHT,
	INLINE_CARD_CREATE_ROW_HEIGHT,
	UNASSIGNED_EPIC_ID,
	UNASSIGNED_USER,
} from '../../../common/constants/index.tsx';
import { isViewSettingAsPanelExpEnabledWithNoExposure } from '../../../feature-flags.tsx';
import {
	ACTIVE_SPRINT,
	BACKLOG,
	BOARD,
	GROUP_TYPE,
	INACTIVE_SPRINT,
	SPRINT,
} from '../../../model/constants.tsx';
import {
	ASSIGNEE,
	ISSUE_PARENT,
	ISSUE_TYPE,
	LABEL,
	TEXT,
	VERSION,
	CUSTOM_FILTER,
} from '../../../model/filters/constants.tsx';
import type { Filters, FilterType } from '../../../model/filters/types.tsx';
import {
	type PartialCardForVersionsPanel,
	type Card,
	type CardId,
	type CardKey,
	type CardListAnalyticsType,
	type CardListId,
	type CardList,
	type PartialCard,
	type Person,
	type CardForFlagWithCommentModal,
	type CardEntry,
	type CardEntryGroup,
	isCardEntryGroup,
} from '../../../model/types.tsx';
import type { CardListOrderEntityState } from '../../reducers/entities/card-list-order/types.tsx';
import type { CardListEntityState } from '../../reducers/entities/card-list/types.tsx';
import type { CardOrderEntityState } from '../../reducers/entities/card-order/types.tsx';
import type { CardEntityState } from '../../reducers/entities/card/types.tsx';
import type { AutoScrollState } from '../../reducers/ui/cards/auto-scroll/types.tsx';
import type { FilteredCardsIds } from '../../reducers/ui/cards/filtered-cards/types.tsx';
import type { FocusedState } from '../../reducers/ui/cards/focused/types.tsx';
import type { LastSelectedCardKeyState } from '../../reducers/ui/cards/last-selected-card-key/types.tsx';
import type { ListSelectionRelativeCard } from '../../reducers/ui/cards/list-selection-relative-card/types.tsx';
import type { SelectedState } from '../../reducers/ui/cards/selected/types.tsx';
import type { DetailViewState } from '../../reducers/ui/detail-view/types.tsx';
import type { State } from '../../types.tsx';
import { getIsCompanyManaged, isCmpKanbanProject, isKanbanBacklogStrategy } from '../app/index.tsx';
import { getCardTypes, getBaseLevelCardTypes, getCardTypesForFilter } from '../card-type/index.tsx';
import { getEntities } from '../entities/index.tsx';
import { filter, getCMPFilters, getFilters } from '../filters/index.tsx';
import { getIssueParents, issueParentIdsSelector } from '../issue-parent/index.tsx';
import { getCurrentUser } from '../user/index.tsx';
import { getEntitiesVersions, getVersionsOrder } from '../version/index.tsx';

export const getCardLists = (state: State): CardListEntityState => getEntities(state).cardList;
export const getCards = (state: State): CardEntityState => getEntities(state).card;

export const isBacklogEmptySelector = createSelector(
	[getCards],
	(cards) => Object.keys(cards).length === 0,
);

export const getCardListsKeys = createSelector([getCardLists], (cardLists) =>
	Object.keys(cardLists),
);

export const getCardsCount = createSelector([getCards], (cards) => Object.keys(cards).length);

const getCardById = (cards: CardEntityState, cardId: CardId): Card | undefined =>
	cards[String(cardId)];

export const makeGetCardAssignee = createSelector([getCards], (cards) => (cardId: number) => {
	const card = cards[cardId];
	return card ? card.assignee : null;
});

export const getCardColor = (state: State, cardId: CardId): string | undefined => {
	const cards = getCards(state);
	const color = getCardById(cards, cardId)?.color;
	if (isNil(color) || isEmpty(trim(color))) {
		return undefined;
	}
	return trim(color);
};

export const getCardEpicColor = (state: State, cardId: CardId): string | undefined => {
	const cards = getCards(state);
	const color = getCardById(cards, cardId)?.epicColor;
	if (isNil(color) || isEmpty(trim(color))) {
		return undefined;
	}
	return trim(color);
};

export const makeGetCardTypeHierarchyLevel = createSelector(
	[getCards, getIssueParents, getBaseLevelCardTypes], // change to getCardTypes when ready to receive all card types
	(cards, parents, allCardTypes) => (cardId: CardId) => {
		const card = cards[cardId];

		if (card) {
			const cardType = allCardTypes.find((it) => it.id === card.typeId);
			return cardType?.typeHierarchyLevel ?? BASE_HIERARCHY_TYPE;
		}

		const parent = parents[cardId];
		return parent ? PARENT_HIERARCHY_TYPE : BASE_HIERARCHY_TYPE;
	},
);

export const hasCardExtraFields = createSelector([getCards], (cards) =>
	some(cards, (card) => (card?.extraFields ? card.extraFields.length > 0 : false)),
);

export const getCardExtraFields = createSelector([getCards], (cards) => {
	const extraFieldsMap = new Map<string, ExtraField>();

	Object.values(cards).forEach((card) => {
		card.extraFields?.forEach((field) => extraFieldsMap.set(field.id, field));
	});

	return Array.from(extraFieldsMap.values());
});

export const getCardListCount = (state: State): number => {
	const { cardList } = getEntities(state);
	return Object.keys(cardList).length;
};

export const getOrderedCardIdsByCardListId = (state: State): CardOrderEntityState =>
	getEntities(state).cardOrder;

export const makeOrderedCardIdsByCardListIdSelector = createSelector(
	[getOrderedCardIdsByCardListId],
	(orderedCardIdsByCardListId) =>
		memoize((cardListId: CardListId) => orderedCardIdsByCardListId?.[cardListId] || []),
);

export const makeOrderedCardIdsByCardListIdExcludingSubtasks = createSelector(
	[getCards, getOrderedCardIdsByCardListId],
	(allCards, orderedCardIdsByCardListId) =>
		memoize((cardListId: CardListId) => {
			const cardIds = orderedCardIdsByCardListId?.[cardListId];
			if (!cardIds) {
				return [];
			}
			return cardIds.filter(
				(cardId) => !isSubtaskHierarchyLevel(allCards[String(cardId)]?.typeHierarchyLevel),
			);
		}),
);

export const getOrderedCardIdsWithLimit = (
	cardOrder: CardOrderEntityState,
	cardListOrder: CardListOrderEntityState,
	limit = 50,
): CardOrderEntityState => {
	let currentCount = 0;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const truncatedCardIds: Record<string, any> = {};
	cardListOrder.forEach((listId) => {
		const cardIds = cardOrder[listId];
		if (!cardIds) return;
		const remaining = limit - currentCount;
		const sliceAmount = Math.min(remaining, cardIds.length);
		const firstCards = cardIds.slice(0, sliceAmount);

		truncatedCardIds[listId] = firstCards;
		currentCount += sliceAmount;
	});
	return truncatedCardIds;
};

export const getOrderedCardListIds = (state: State): CardListOrderEntityState =>
	getEntities(state).cardListOrder;

export const getOrderedCardIdsByCardListIdWithLimit = createSelector(
	[getOrderedCardIdsByCardListId, getOrderedCardListIds],
	(cardOrder, cardListOrder) => getOrderedCardIdsWithLimit(cardOrder, cardListOrder),
);

export const makeOrderedCardIdsByCardListIdWithLimitExcludingSubtasks = createSelector(
	[getCards, getOrderedCardIdsByCardListId, getOrderedCardListIds],
	(allCards, orderedCardIdsByCardListId, cardListOrder) => {
		const initialResult = getOrderedCardIdsWithLimit(orderedCardIdsByCardListId, cardListOrder);
		const result: { [key: string]: CardId[] } = {};
		cardListOrder.forEach((cardListId) => {
			const cardIds = initialResult[cardListId] ?? [];
			result[cardListId] = cardIds.filter(
				(cardId) => !isSubtaskHierarchyLevel(allCards[String(cardId)].typeHierarchyLevel),
			);
		});

		return memoize((cardListId: CardListId) => result[cardListId] ?? []);
	},
);

export const getSelectedCards = (state: State): SelectedState => state.ui.cards.selected;
export const getLastSelectedCardKey = (state: State): LastSelectedCardKeyState =>
	state.ui.cards.lastSelectedCardKey;

export const getDetailViewData = (state: State): DetailViewState => state.ui.detailView;
export const getListSelectionRelativeCard = (state: State): ListSelectionRelativeCard =>
	state.ui.cards.listSelectionRelativeCard;

export const getFocusedCard = (state: State): FocusedState => state.ui.cards.focused;
export const getFilteredCardIds = (state: State): FilteredCardsIds =>
	state.ui.cards.filteredCards.filteredCardsIds;
export const isFilteredCardsLoading = (state: State): boolean =>
	state.ui.cards.filteredCards.isLoading;

export const isStatusFieldsHidden = (state: State): boolean =>
	state.ui.cards.fields.isStatusFieldsHidden;

export const isVersionFieldsHidden = (state: State): boolean =>
	state.ui.cards.fields.isVersionFieldsHidden;

export const isEpicFieldsHidden = (state: State): boolean =>
	state.ui.cards.fields.isEpicFieldsHidden;
export const isCardSelected = createSelector(
	getSelectedCards,
	(selectedCards) => (cardId: CardId) => selectedCards.includes(cardId),
);

export const getCardAutoScroll = (state: State): AutoScrollState => state.ui.cards.autoScroll;

// Active card is either the focused card of the most recently selected card
export const getActiveCard = (state: State): CardId | undefined => {
	const focusedCard = getFocusedCard(state);
	if (focusedCard !== null) {
		return focusedCard;
	}
	const selectedCards = getSelectedCards(state);
	return selectedCards[selectedCards.length - 1];
};

export const getActiveCardKey = (state: State): CardKey | undefined => {
	const activeCardId = getActiveCard(state);
	const allCards = getCards(state);
	return allCards[String(activeCardId)] && allCards[String(activeCardId)].key;
};

export const getActiveCardAssignee = (state: State): Person | null => {
	const activeCardId = getActiveCard(state);
	const allCards = getCards(state);
	return allCards[String(activeCardId)] && allCards[String(activeCardId)].assignee;
};

export const cardsSelector = createSelector(getCards, (cards) => Object.values(cards));

export const getCardIds = createSelector(cardsSelector, (cards) => cards.map((card) => card.id));

export const isDetailViewOpenWithAnySelectedCardSelector = createSelector(
	[getDetailViewData, getSelectedCards],
	(detailView, selectedCards) => detailView && selectedCards.indexOf(detailView.cardId) > -1,
);

export const getOrderedAssignees = createSelector(
	[getCards, getCurrentUser, getIsCompanyManaged],
	(allCards, currentUser, isCompanyManaged) => {
		const currentUserId = currentUser ? currentUser.id : null;
		if (isCompanyManaged && fg('backlog-assignee-filter-sort-fix')) {
			const assignees = Array.from(
				// Makes assignees as basically a Set
				Object.values(allCards)
					.reduce<Map<string, Person>>((people, card) => {
						if (card.assignee) {
							people.set(card.assignee.id, card.assignee);
						}
						return people;
					}, new Map())
					.values(),
			);
			const assigneeList: Person[] = assignees
				.filter((person) => person.id !== currentUserId)
				.sort((a, b) => a.displayName.localeCompare(b.displayName));

			const cUser = currentUser?.avatarUrl
				? currentUser
				: assignees.find((assignee) => assignee.id === currentUser?.id);
			if (cUser) {
				assigneeList.unshift(cUser);
			}

			return freeze(assigneeList);
		}

		const assigneeList: Person[] = Object.values(allCards)
			.map((card) => card.assignee) // eslint-disable-next-line @typescript-eslint/no-explicit-any
			.reduce<Array<any>>((people: Person[], person: Person | null) => {
				if (person && person.id !== currentUserId) {
					people.push(person);
				}
				return people;
			}, []);

		const uniqueAssigneeList: Person[] = uniqBy(assigneeList, (assignee) => assignee.id);
		if (isCompanyManaged) {
			uniqueAssigneeList.sort((a, b) => a.displayName.localeCompare(b.displayName));
		} else {
			const assigneeCounts = countBy(assigneeList, (assignee) => assignee.id);
			uniqueAssigneeList.sort(
				(a: Person, b: Person) => assigneeCounts[b.id] - assigneeCounts[a.id],
			);
		}

		if (currentUser) {
			uniqueAssigneeList.unshift(currentUser);
		}

		return freeze(uniqueAssigneeList);
	},
);

export const getOrderedLabels = createSelector([getCards], (allCards) =>
	uniq(
		Object.values(allCards).reduce((labels, card) => {
			if (card.labels && card.labels.length > 0) {
				labels.push(...card.labels);
			}
			return labels;
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		}, [] as string[]),
	).sort((a, b) => alphabeticalSort(a, b)),
);

export const getOrderedVersions = createSelector([getCards], (allCards) =>
	uniq(
		Object.values(allCards).reduce(
			(fixVersions, card) =>
				card.fixVersions && card.fixVersions.length > 0
					? fixVersions.concat(card.fixVersions)
					: fixVersions,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			[] as string[],
		),
	).sort((a, b) => alphabeticalSort(a, b)),
);

export const getOrderedVersionsRelay = createSelector([getVersionsOrder], (versions) =>
	versions.concat().sort((a, b) => alphabeticalSort(a, b)),
);

export const cardTypeIdsSelector = createSelector([getCards], (allCards) =>
	uniq(Object.values(allCards).map((card) => String(card.typeId))),
);

export const getOrderedCardTypes = createSelector(
	[cardTypeIdsSelector, getCardTypes],
	(uniqCardTypeIds, allCardTypes) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const uniqCardTypes = uniqCardTypeIds.reduce<Array<any>>((cardTypes, typeId) => {
			const foundCardType = allCardTypes.find((cardType) => String(cardType.id) === typeId);
			if (foundCardType) {
				cardTypes.push({
					id: String(foundCardType.id),
					name: foundCardType.name,
					iconUrl: foundCardType.iconUrl,
					typeHierarchyLevel: foundCardType.typeHierarchyLevel ?? null,
				});
			}
			return cardTypes;
		}, []);

		return uniqCardTypes.sort((a, b) => alphabeticalSort(a.name, b.name));
	},
);

export const getLastSelectedCard = createSelector(
	[getSelectedCards],
	(cardSelection) => cardSelection.slice(-1)[0] || null,
);

export const willDetailViewShow = createSelector(
	[getLastSelectedCardKey],
	(lastSelectedCardKey) => lastSelectedCardKey !== '',
);

/**
 * Get the key by id for any card present on the backlog
 * as well as issue parent cards that are not mapped to
 * the backlog but are referenced by child issues
 */
export const makeCardKeyFromCardId = createSelector(
	[getCards, getIssueParents],
	(cards, issueParents) => {
		const unmappedParents: {
			[parentId: number]: {
				key: string;
			};
		} = {};

		Object.values(cards).forEach(({ parentId, parentKey }) => {
			if (
				parentId &&
				parentKey &&
				!cards[parentId] &&
				!issueParents[parentId] &&
				!unmappedParents[parentId]
			) {
				unmappedParents[parentId] = { key: parentKey };
			}
		});

		return (cardId: number) =>
			(cards[cardId] || issueParents[cardId] || unmappedParents[cardId])?.key ?? null;
	},
);

export const makeCardIdFromCardKey = createSelector([getCards], (allCards) =>
	memoize((cardKey) => {
		const cardIdWithKey = Object.keys(allCards).find((cardId) => allCards[cardId]?.key === cardKey);
		return Number(cardIdWithKey);
	}),
);

export const getFilterFunction = createSelector(
	[getFilters, getFilteredCardIds, getCards],
	(filterValues, filteredCardIds, allCards) =>
		filter(filterValues, filteredCardIds || undefined, Object.values(allCards)),
);

export const makeOrderedVisibleCardsForCardList = createSelector(
	[getCards, getOrderedCardIdsByCardListId, getFilterFunction],
	(allCards, orderedCardIdsLists, filterFunction) => {
		const getOrderedVisibleCards = (cardListId: string): Card[] => {
			const cardIds = orderedCardIdsLists[cardListId];
			if (!cardIds) return [];
			const unfilteredList = cardIds
				.filter((cardId) => allCards[String(cardId)])
				.map((cardId) => allCards[String(cardId)]);
			return filterFunction(unfilteredList);
		};

		return memoize(getOrderedVisibleCards);
	},
);

export const makeOrderedVisibleCardsExcludingSubtasksForCardList = createSelector(
	[getCards, getOrderedCardIdsByCardListId, getFilterFunction],
	(allCards, orderedCardIdsLists, filterFunction) => {
		const getOrderedVisibleCardsExcludingSubtasks = (cardListId: string): Card[] => {
			const cardIds = orderedCardIdsLists[cardListId];
			if (!cardIds) return [];
			const unfilteredList = cardIds
				.filter(
					(cardId) =>
						allCards[String(cardId)] &&
						!isSubtaskHierarchyLevel(allCards[String(cardId)].typeHierarchyLevel),
				)
				.map((cardId) => allCards[String(cardId)]);
			return filterFunction(unfilteredList);
		};

		return memoize(getOrderedVisibleCardsExcludingSubtasks);
	},
);

export const makeOrderedVisibleCardsForCardListWithLimit = createSelector(
	[getCards, getOrderedCardIdsByCardListIdWithLimit, getFilterFunction],
	(allCards, orderedCardIdsLists, filterFunction) =>
		memoize((cardListId) => {
			const cardIds = orderedCardIdsLists[cardListId];
			if (!cardIds) return [];
			const unfilteredList = cardIds
				.filter((cardId) => allCards[String(cardId)])
				.map((cardId) => allCards[String(cardId)]);
			return filterFunction(unfilteredList);
		}),
);

export const makeOrderedVisibleCardsExcludingSubtasksForCardListWithLimit = createSelector(
	[getCards, getOrderedCardIdsByCardListIdWithLimit, getFilterFunction],
	(allCards, orderedCardIdsLists, filterFunction) =>
		memoize((cardListId) => {
			const cardIds = orderedCardIdsLists[cardListId];
			if (!cardIds) return [];
			const unfilteredList = cardIds
				.filter(
					(cardId) =>
						allCards[String(cardId)] &&
						!isSubtaskHierarchyLevel(allCards[String(cardId)].typeHierarchyLevel),
				)
				.map((cardId) => allCards[String(cardId)]);

			return filterFunction(unfilteredList);
		}),
);

export const getFilteredOrderedCardIdsByCardListId = createSelector(
	[getCards, getOrderedCardIdsByCardListId, getFilterFunction],
	(allCards, orderedCardIdsLists, filterFunction): CardOrderEntityState =>
		Object.fromEntries(
			Object.entries(orderedCardIdsLists).map(([key, cardIds]) => {
				const unfilteredList = cardIds
					.filter((cardId) => allCards[String(cardId)])
					.map((cardId) => allCards[String(cardId)]);
				const filteredIds = filterFunction(unfilteredList).map((card) => card.id);
				return [key, filteredIds];
			}),
		),
);

export const makeOrderVisibleCardIdsForCardListWithLimit = createSelector(
	[makeOrderedVisibleCardsForCardListWithLimit],
	(getOrderedVisibleCardsForCardList) =>
		memoize((cardListId) => getOrderedVisibleCardsForCardList(cardListId).map((card) => card.id)),
);

export const shouldShowDetailView = createSelector(
	[getDetailViewData],
	(detailView) => detailView !== null,
);

export const makeOrderedVisibleCardIdsForCardList = createSelector(
	[makeOrderedVisibleCardsForCardList],
	(getOrderedVisibleCardsForCardList) => {
		const getOrderedVisibileCardIds = (cardListId: string): CardId[] =>
			getOrderedVisibleCardsForCardList(cardListId).map((card) => card.id);
		return memoize(getOrderedVisibileCardIds);
	},
);

export const makeOrderedVisibleCardEntriesOld = createSelector(
	[makeOrderedVisibleCardsForCardList],
	(getOrderedVisibleCardsForCardList) => {
		const getOrderedVisibileCardIds = (cardListId: string): CardEntry[] =>
			getOrderedVisibleCardsForCardList(cardListId).map((card) => ({
				type: 'card',
				cardId: card.id,
			}));
		return memoize(getOrderedVisibileCardIds);
	},
);

export const getExpandedCardGroups = (state: State) => state.ui.cardList.cardGroupExpanded;

export const makeGetCardGroupExpanded = createSelector(
	[getCards, getExpandedCardGroups],
	(cards, expandedCardGroups) => {
		const getCardGroupExpanded = (cardId: number) => {
			const card = getCardById(cards, cardId);

			if (card && card.parentId && isSubtaskHierarchyLevel(card?.typeHierarchyLevel)) {
				// you passed in a subtask, checking if the parent is expanded
				return expandedCardGroups[card.parentId];
			}
			return expandedCardGroups[cardId];
		};

		return memoize(getCardGroupExpanded);
	},
);

export const getParentIdByChildIdSelector = createSelector([getCards], (cards) => {
	const getParentId = (cardId: number): number | undefined =>
		getCardById(cards, cardId)?.parentId ?? undefined;
	return memoize(getParentId);
});

export const getChildIdsByParentIdSelector = createSelector([getCards], (cards) => {
	const cardsByParent = groupBy(cards, (card) => get(card, 'parentId', 'NO_PARENT'));
	const cardsWithParent = omit(cardsByParent, 'NO_PARENT');
	return mapValues(cardsWithParent, (childCards: Card[]) => childCards.map((card) => card.id));
});

export const makeGetCardHierarchyLevel = createSelector([getCards], (cards) => {
	const getHierarchyLevel = (cardId: number): number | undefined =>
		getCardById(cards, cardId)?.typeHierarchyLevel ?? undefined;
	return memoize(getHierarchyLevel);
});

// Returns all unfiltered card entries
export const makeGetOrderedCardEntries = createSelector(
	[getParentIdByChildIdSelector, makeOrderedVisibleCardsForCardList, makeGetCardGroupExpanded],
	(getParentId, getOrderedVisibleCardsForCardList, getIsGroupExpanded) => {
		const getOrderedVisibileCardIds = (cardListId: string): CardEntry[] => {
			const cardEntries: CardEntry[] = [];
			const cards = getOrderedVisibleCardsForCardList(cardListId);
			const cardIds = cards.map((card) => card.id);
			const nonParentCardIds = cards
				.filter((card) => !isSubtaskHierarchyLevel(card.typeHierarchyLevel))
				.map((card) => card.id);
			const parentCardIds = cards
				.filter(
					(card) => isSubtaskHierarchyLevel(card.typeHierarchyLevel) && !!getParentId(card.id),
				)
				.map((card) => card.id);
			const parentCardIdsByChild = groupBy(parentCardIds, getParentId);
			const childCardEntries: [number[]] = [[]];
			cardIds?.forEach((cardId) => {
				if (nonParentCardIds.includes(cardId)) {
					cardEntries.push({ type: 'card', cardId });
					return;
				}
				const parentId = getParentId(cardId);

				if (!parentId || !(parentId in parentCardIdsByChild)) return;

				const isGroupCollapsed = !getIsGroupExpanded(parentId);

				const parent = cardEntries.find(
					(entry) => !isCardEntryGroup(entry) && entry.cardId === parentId,
				);
				// If parent and subtasks are in the same list find parent index and insert group next to it
				if (nonParentCardIds.includes(parentId)) {
					const isAttachedToParent = nonParentCardIds.includes(parentId);
					if (parent !== undefined) {
						const parentIndex = cardEntries.indexOf(parent);
						cardEntries.splice(parentIndex + 1, 0, {
							type: 'group',
							parentId,
							cardIds: parentCardIdsByChild[parentId],
							isAttachedToParent,
							// Only card groups with a parent are able to be visually collapsed
							isCollapsed: isGroupCollapsed && isAttachedToParent,
						});
					} else {
						childCardEntries.push(parentCardIdsByChild[parentId]);
					}
				} else {
					const isAttachedToParent = nonParentCardIds.includes(parentId);
					cardEntries.push({
						type: 'group',
						parentId,
						cardIds: parentCardIdsByChild[parentId],
						isAttachedToParent,
						isCollapsed: isGroupCollapsed && isAttachedToParent,
					});
				}

				delete parentCardIdsByChild[parentId];
			});
			if (childCardEntries.length > 0) {
				childCardEntries.forEach((subtask) => {
					const parentId = getParentId(subtask[0]);
					const parent = cardEntries.find(
						(entry) => !isCardEntryGroup(entry) && entry.cardId === parentId,
					);
					if (parent !== undefined && parentId !== undefined) {
						const parentIndex = cardEntries.indexOf(parent);
						const isCollapsed = !getIsGroupExpanded(parentId);
						cardEntries.splice(parentIndex + 1, 0, {
							type: 'group',
							parentId,
							cardIds: subtask,
							isAttachedToParent: true,
							isCollapsed,
						});
					}
				});

				return cardEntries;
			}
			return cardEntries;
		};
		return memoize(getOrderedVisibileCardIds);
	},
);

// returns all visible card groups that are attached to parents
export const makeGetAllAttachedToParentCardGroupEntries = createSelector(
	[getCardListsKeys, makeGetOrderedCardEntries],
	(cardListKeys, getOrderedCardEntries) => {
		const getAllAttachedToParentCardGroupEntries = (): CardEntryGroup[] => {
			let cardEntryGroups: CardEntryGroup[] = [];
			cardListKeys.forEach((cardListId) => {
				const cardEntries: CardEntry[] = getOrderedCardEntries(cardListId);
				const groupCardEntries: CardEntryGroup[] = cardEntries.filter(isCardEntryGroup);
				const attachedToParentGroupCardEntries: CardEntryGroup[] = groupCardEntries.filter(
					(cardEntry) => cardEntry.isAttachedToParent,
				);
				cardEntryGroups = cardEntryGroups.concat(attachedToParentGroupCardEntries);
			});

			return cardEntryGroups;
		};
		return memoize(getAllAttachedToParentCardGroupEntries);
	},
);

// Returns all unfiltered and non-collapsed card entries
export const makeOrderedVisibleCardEntries = createSelector(
	[makeGetOrderedCardEntries],
	(getOrderedCardEntries) => {
		const getOrderedVisibileCardIds = (cardListId: string): CardEntry[] => {
			const cardEntries = getOrderedCardEntries(cardListId);
			return cardEntries.filter((cardEntry) => {
				if (isCardEntryGroup(cardEntry)) {
					return !cardEntry.isCollapsed;
				}
				return true;
			});
		};

		return memoize(getOrderedVisibileCardIds);
	},
);

// Returns unfiltered cards that
// are not in a collapsed card group
export const makeGetVisibleCardOrderFromCardEntries = createSelector(
	[makeOrderedVisibleCardEntries],
	(cardEntries) => {
		const getVisibleCardOrderFromCardEntries = (cardListId: string): CardId[] => {
			const currentCardEntries = cardEntries(cardListId);
			return currentCardEntries.flatMap((entry) =>
				isCardEntryGroup(entry) ? entry.cardIds : entry.cardId,
			);
		};
		return memoize(getVisibleCardOrderFromCardEntries);
	},
);

export const getVisibleCardIds = createSelector(
	[makeOrderedVisibleCardIdsForCardList, getOrderedCardListIds],
	(getOrderedVisibleCardIdsForCardList, cardListIds: string[]) =>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		cardListIds.reduce<Array<any>>(
			(allCards, id) => allCards.concat(getOrderedVisibleCardIdsForCardList(id)),
			[],
		),
);

export const makeIsCardVisible = createSelector(
	[getCards, getFilterFunction],
	(allCards, filterFunction) =>
		(id: CardId): boolean => {
			const card = allCards[`${id}`];
			if (!card) return false;
			const list = filterFunction([card]);
			return list.length !== 0;
		},
);

export const makeIsCardOnBoard = createSelector(
	[getOrderedCardIdsByCardListId],
	(cardIdsByCardList) => {
		const getIsCardOnBoard = (id: CardId): boolean =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			get(cardIdsByCardList, [BOARD], [] as number[]).includes(id);

		return getIsCardOnBoard;
	},
);

export const makeGetCardListIdByCardId = createSelector(
	[getOrderedCardIdsByCardListId],
	(cardIdsByCardList) => {
		const getCardListIdByCardId = (id: CardId): string | undefined =>
			Object.keys(cardIdsByCardList).find((cardListId) =>
				cardIdsByCardList[cardListId].includes(id),
			);

		return getCardListIdByCardId;
	},
);

export const makeGetFastVirtualListMinimumOffSetByCardListId = createSelector(
	[getOrderedCardListIds, makeOrderedVisibleCardIdsForCardList],
	(cardListIds, orderedVisibleCardIdsForCardList) => {
		const getFastVirtualListMinimumOffSetByCardListId = (cardListId: CardListId): number => {
			const cardListIndex = cardListIds.findIndex((id) => id === cardListId);
			let offset = 0;

			for (let i = 0; i < cardListIndex; i += 1) {
				offset += orderedVisibleCardIdsForCardList(cardListIds[i]).length * 40;
			}

			return offset;
		};

		return getFastVirtualListMinimumOffSetByCardListId;
	},
);

export const makeGetCardListAnalyticsTypeById = createSelector([getCardLists], (cardLists) =>
	memoize((id) => {
		if (id === BACKLOG) {
			return BACKLOG;
		}
		// If its a sprint, figure out whether its an active one or not, else kanban board
		const card = cardLists[id];
		if (!isNil(card) && card.type === SPRINT && !isNil(card.sprintData)) {
			return card.sprintData.state === 'ACTIVE' ? ACTIVE_SPRINT : INACTIVE_SPRINT;
		}
		// Default to null or we will get skewed analytics
		return id === BOARD ? BOARD : null;
	}),
);

export const makeGetTopCardIdFromCardList = createSelector(
	[getOrderedCardIdsByCardListId, getCards],
	(cardIdsByCardList, allCards) =>
		(cardListId: string, ignoredIds: number[] = []) => {
			const list = difference(cardIdsByCardList[cardListId], ignoredIds);
			const cardId = list.length > 0 ? list[0] : null;
			if (cardId && isSubtaskHierarchyLevel(allCards[String(cardId)]?.typeHierarchyLevel)) {
				return allCards[String(cardId)].parentId;
			}
			return cardId;
		},
);

export const makeGetBottomCardIdFromCardList = createSelector(
	[getOrderedCardIdsByCardListId],
	(cardIdsByCardList) =>
		(cardListId: string, ignoredIds: number[] = []) => {
			const list = difference(cardIdsByCardList[cardListId], ignoredIds);
			return list.length > 0 ? list[list.length - 1] : null;
		},
);

export const hasCardTypesSelector = createSelector(
	[getBaseLevelCardTypes], // will not include Epic, Subtask or custom level cardTypes
	(cardTypes) => cardTypes?.length > 0,
);

export const getCollapsedCardLists = (state: State) => state.ui.cardList.collapsed;

export const makeIsCardListCollapsed = createSelector(
	[getCollapsedCardLists],
	(collapsedCardLists) => (cardListId: string) => !!collapsedCardLists[cardListId],
);

export const getVisibleCardListIds = createSelector(
	[getCardLists, getOrderedCardListIds],
	(cardLists, ids) => ids.filter((id) => !cardLists[id]?.isOptimistic),
);

export const getVisibleSprintCardLists = createSelector(
	[getCardLists, getOrderedCardListIds],
	(cardLists, ids) =>
		ids.reduce<CardList[]>((accVisibleSprintCardLists, id) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const card = cardLists[id] as CardList | undefined;

			if (card && !card.isOptimistic && card.type === SPRINT) {
				accVisibleSprintCardLists.push(card);
			}

			return accVisibleSprintCardLists;
		}, []),
);

export const isAnyOptimisticCardList = createSelector(
	[getOrderedCardListIds, getVisibleCardListIds],
	(all, visible) => all.length !== visible.length,
);

export const makeGetNextCardListId = createSelector([getVisibleCardListIds], (ids) => {
	const getNextCardListId = (cardListId: string): string | null => {
		const index = ids.indexOf(cardListId);
		if (index === -1 || index === ids.length - 1) {
			return null;
		}
		return ids[index + 1];
	};

	return getNextCardListId;
});

export const getFlattenOrderedCardIds = createSelector(
	[getOrderedCardIdsByCardListId, getVisibleCardListIds],
	(cardIdsByCardListId, cardListIds) =>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		cardListIds.reduce<Array<any>>((cardIds, cardListId) => {
			cardIds.push(...cardIdsByCardListId[cardListId]);
			return cardIds;
		}, []),
);

export const getCardListDisabled = (state: State) => state.ui.cardList.disabled;

export const makeIsCardListDisabled = createSelector([getCardListDisabled], (disabled) => {
	const getIsCardListDisabled = (cardListId: string): boolean => Boolean(disabled[cardListId]);

	return getIsCardListDisabled;
});

const getMaxColumnLimitFromCardList = (cardList: CardList) => cardList.maxColumnLimit;
const getMinColumnLimitFromCardList = (cardList: CardList) => cardList.minColumnLimit;

const makeGetColumnLimit = (columnLimitSelector: (cardList: CardList) => number | undefined) =>
	createSelector(
		[getCardLists, getIsCompanyManaged, isKanbanBacklogStrategy],
		(cardLists: CardListEntityState, isCompanyManaged: boolean, isKanban: boolean) => {
			const getColumnLimit = (cardListId: string): number | undefined =>
				isCompanyManaged &&
				isKanban &&
				cardLists[cardListId] &&
				columnLimitSelector(cardLists[cardListId])
					? columnLimitSelector(cardLists[cardListId])
					: undefined;

			return getColumnLimit;
		},
	);
export const makeGetMaxColumnLimit = makeGetColumnLimit(getMaxColumnLimitFromCardList);

export const makeGetMinColumnLimit = makeGetColumnLimit(getMinColumnLimitFromCardList);

export const validFilterValuesSelector = createSelector(
	[
		getFilters,
		getOrderedAssignees,
		getOrderedLabels,
		issueParentIdsSelector,
		cardTypeIdsSelector,
		getOrderedVersionsRelay,
		getCMPFilters,
		getIsCompanyManaged,
	],
	(
		filters,
		validAssignees,
		validLabels,
		validIssueParentIds,
		validIssueTypeIds,
		validVersions,
		validCustomFilters,
		isCompanyManaged,
	) => {
		const filterTypeWhitelist = [
			LABEL,
			ASSIGNEE,
			TEXT,
			ISSUE_PARENT,
			ISSUE_TYPE,
			VERSION,
			'CUSTOM_FILTER',
		];

		const validIssueParentIdsWithUnassignedOption = [
			String(UNASSIGNED_EPIC_ID),
			...validIssueParentIds,
		];
		const validAssigneesWithUnassignedOption = [UNASSIGNED_USER, ...validAssignees];
		const validVersionsWithUnassignedOption = [UNASSIGNED_VERSION_ID, ...validVersions];

		const validFilters: Partial<Filters> = {};

		if (filters[ASSIGNEE]) {
			const allValidAssignees = isCompanyManaged
				? validAssigneesWithUnassignedOption
				: validAssignees;

			const inter = intersection(
				allValidAssignees.map((person) => person.id),
				filters[ASSIGNEE] ?? [],
			);

			if (inter.length > 0) {
				validFilters[ASSIGNEE] = inter;
			}
		}

		if (filters[LABEL]) {
			const inter = intersection(validLabels, filters[LABEL]);
			if (inter.length > 0) {
				validFilters[LABEL] = inter;
			}
		}

		if (filters[ISSUE_PARENT]) {
			// Ignore the epic/issue parent filter validation in CMP
			if (isCompanyManaged) {
				validFilters[ISSUE_PARENT] = filters[ISSUE_PARENT];
			} else {
				const inter = intersection(
					validIssueParentIdsWithUnassignedOption,
					filters[ISSUE_PARENT] ?? [],
				);

				if (inter.length > 0) {
					validFilters[ISSUE_PARENT] = inter;
				}
			}
		}

		if (filters[ISSUE_TYPE]) {
			const inter = intersection(validIssueTypeIds, filters[ISSUE_TYPE] ?? []);
			if (inter.length > 0) {
				validFilters[ISSUE_TYPE] = inter;
			}
		}

		if (filters[VERSION]) {
			const allValidVersions = isCompanyManaged ? validVersionsWithUnassignedOption : validVersions;

			const inter = intersection(allValidVersions, filters[VERSION] ?? []);

			if (inter.length > 0) {
				validFilters[VERSION] = inter;
			}
		}

		if (isCompanyManaged && filters[CUSTOM_FILTER]) {
			const inter = intersection(
				filters[CUSTOM_FILTER],
				validCustomFilters.map(({ id }) => id),
			);
			if (inter.length > 0) {
				validFilters[CUSTOM_FILTER] = inter;
			}
		}

		Object.keys(filters)
			.filter((filterType) => !filterTypeWhitelist.includes(filterType))
			.forEach((notAllowedFilter) => {
				throw new Error(`Please whitelist filter '${notAllowedFilter}' in filter selectors`);
			});

		return validFilters;
	},
);

export const invalidFilterValuesSelector = createSelector(
	[
		getFilters,
		getOrderedAssignees,
		getOrderedLabels,
		issueParentIdsSelector,
		cardTypeIdsSelector,
		getOrderedVersionsRelay,
		getCMPFilters,
		getIsCompanyManaged,
	],
	(
		filters,
		validAssignees,
		validLabels,
		validIssueParentIds,
		validIssueTypeIds,
		validVersions,
		validCustomFilters,
		isCompanyManaged,
	) => {
		const filterTypeWhitelist = [
			LABEL,
			ASSIGNEE,
			TEXT,
			ISSUE_PARENT,
			ISSUE_TYPE,
			VERSION,
			CUSTOM_FILTER,
		];

		const validIssueParentIdsWithUnassignedOption = [
			String(UNASSIGNED_EPIC_ID),
			...validIssueParentIds,
		];
		const validAssigneesWithUnassignedOption = [UNASSIGNED_USER, ...validAssignees];
		const validVersionsWithUnassignedOption = [UNASSIGNED_VERSION_ID, ...validVersions];

		const invalidFilters: Partial<Filters> = {};

		if (filters[ASSIGNEE]) {
			const allValidAssignees = validAssigneesWithUnassignedOption;

			const diff = difference(
				filters[ASSIGNEE],
				allValidAssignees.map((person) => person.id),
			);

			if (diff.length > 0) {
				invalidFilters[ASSIGNEE] = diff;
			}
		}

		if (filters[LABEL]) {
			const diff = difference(filters[LABEL], validLabels);
			if (diff.length > 0) {
				invalidFilters[LABEL] = diff;
			}
		}

		// Ignore the epic/issue parent filter validation in CMP
		if (filters[ISSUE_PARENT] && !isCompanyManaged) {
			const diff = difference(filters[ISSUE_PARENT], validIssueParentIdsWithUnassignedOption);

			if (diff.length > 0) {
				invalidFilters[ISSUE_PARENT] = diff;
			}
		}

		if (filters[ISSUE_TYPE]) {
			const diff = difference(filters[ISSUE_TYPE], validIssueTypeIds);
			if (diff.length > 0) {
				invalidFilters[ISSUE_TYPE] = diff;
			}
		}

		if (filters[VERSION]) {
			const allValidVersions = isCompanyManaged ? validVersionsWithUnassignedOption : validVersions;

			const diff = difference(filters[VERSION], allValidVersions);

			if (diff.length > 0) {
				invalidFilters[VERSION] = diff;
			}
		}

		if (isCompanyManaged && filters[CUSTOM_FILTER]) {
			const diff = difference(
				filters[CUSTOM_FILTER],
				validCustomFilters.map(({ id }) => id),
			);
			if (diff.length > 0) {
				invalidFilters[CUSTOM_FILTER] = diff;
			}
		}

		Object.keys(filters)
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			.filter((filterType) => !filterTypeWhitelist.includes(filterType as FilterType))
			.forEach((notAllowedFilter) => {
				throw new Error(`Please whitelist filter '${notAllowedFilter}' in filter selectors`);
			});

		return invalidFilters;
	},
);

export const hasInvalidFilterValuesSelector = createSelector(
	[invalidFilterValuesSelector],
	(invalidFilterValues) => Object.keys(invalidFilterValues).length > 0,
);

export const selectedCardsForVersionsPanelSelector = createSelector(
	getCards,
	getSelectedCards,
	(cards, cardIds): PartialCardForVersionsPanel[] =>
		cardIds
			.filter((cardId) => cards[String(cardId)])
			.map((cardId) => {
				const { id, key, parentId, projectId, fixVersions, issueTypeName } = cards[String(cardId)];
				return { id, key, parentId, projectId, fixVersions, issueTypeName };
			}),
);

export const selectedPartialCardsWithTypeHierarchyLevelSelector = createSelector(
	getCards,
	getSelectedCards,
	(cards, cardIds) =>
		cardIds
			.filter((cardId) => cards[String(cardId)])
			.map((cardId) => {
				const { id, key, parentId, projectId, typeHierarchyLevel } = cards[String(cardId)];
				return { id, key, parentId, projectId, typeHierarchyLevel };
			}),
);

export const partialCardsFromCardIdsSelector = createSelector(getCards, (cards) => {
	const getPartialCardsFromCardIds = (cardIds: CardId[]): PartialCard[] =>
		cardIds
			.filter((cardId) => cards[String(cardId)])
			.map((cardId) => {
				const { id, key, parentId, projectId, issueTypeName } = cards[String(cardId)];
				return { id, key, parentId, projectId, issueTypeName };
			});

	return memoize(getPartialCardsFromCardIds);
});

export const cardsForFlagWithCommentModalFromCardIdsSelector = createSelector(
	[getCards, getCardTypesForFilter],
	(cards, cardTypes) => {
		const getCardsForFlagWithCommentModalFromCardIds = (
			cardIds: CardId[],
		): CardForFlagWithCommentModal[] =>
			cardIds
				.filter((cardId) => cards[String(cardId)])
				.map((cardId) => {
					const { id, key, summary, typeId } = cards[String(cardId)];
					const type = cardTypes.find((it) => it.id === String(typeId)) ?? {
						id: String(typeId),
						name: '',
						iconUrl: '',
					};
					return { id, key, summary, type };
				});

		return memoize(getCardsForFlagWithCommentModalFromCardIds);
	},
);

export const isAssignedToIssueParent = createSelector(cardsSelector, (cards) => {
	const getIsAssignedToIssueParent = (selectedIds: CardId[]): boolean =>
		cards.some((card) => selectedIds.includes(card.id) && card.parentId);

	return getIsAssignedToIssueParent;
});

export const numTotalIssueChildren = createSelector(cardsSelector, (cards) =>
	cards.reduce((num: number, card: Card): number => num + card.numTotalChildren, 0),
);

export const numCompleteIssueChildren = createSelector(cardsSelector, (cards) =>
	cards.reduce((num: number, card: Card): number => num + card.numCompleteChildren, 0),
);

export const getKeyByIdForAllIssueTypes = createSelector(
	[getCards, getIssueParents],
	(cards, parents) =>
		memoize((cardId) => {
			if (cards[cardId]) {
				return cards[cardId].key;
			}
			if (parents[cardId]) {
				return parents[cardId].key;
			}
			return null;
		}),
);

export const getIdByKeyForAllIssueTypes = createSelector(
	[getCards, getIssueParents],
	(cards, parents) =>
		memoize((key) => {
			const cardId = Object.keys(cards).find((id) => cards[id].key === key);
			if (cardId) {
				return Number(cardId);
			}

			const parentId = Object.keys(parents).find((id) => parents[id].key === key);
			if (parentId) {
				return Number(parentId);
			}

			return null;
		}),
);

export const getCardIdWithAboveICCOpen = (state: State): CardId | null =>
	state.ui.cards.richCard.cardIdWithAboveIccOpen ?? null;

export const hasICCOpen = (
	state: State,
	{
		cardId,
	}: {
		cardId: CardId;
	},
): boolean => cardId === getCardIdWithAboveICCOpen(state);

export const getRichActiveCard = (state: State): CardId | null =>
	state.ui.cards.richCard.mainActiveCard;

export const getAllCardIdsIncludingIssueParents = createSelector(
	[getCardIds, issueParentIdsSelector],
	(cardIds, parentIds) => {
		const formattedParentIds = parentIds.map((id) => Number(id));
		return [...cardIds, ...formattedParentIds] as const;
	},
);

export type CardListCounts = {
	/**
	 * Number of issues on the board
	 */
	boardIssueNo: number;
	/**
	 * Number of issues on the active sprint
	 */
	activeSprintIssueNo: number;
	/**
	 * Number of issues on the backlog
	 */
	backlogIssueNo: number;
	/**
	 * Number of sprints
	 */
	sprintsCount: number;
	/**
	 * Total issues in all sprints
	 */
	sprintsIssueNo: number;
	/**
	 * Average issues per sprint
	 */
	sprintsAverageIssueNo: number;
	/**
	 * Max issues per sprint
	 */
	sprintsMaxIssueNo: number;
};

export const getCardListStats = createSelector(
	[
		getCardLists,
		getOrderedCardListIds,
		getOrderedCardIdsByCardListId,
		makeGetCardListAnalyticsTypeById,
	],
	(
		cardLists: CardListEntityState,
		cardListIds: CardListId[],
		cardIdsByCardListId: CardOrderEntityState,
		getCardListAnalyticsTypeById: (id: CardListId) => CardListAnalyticsType | null,
	): CardListCounts => {
		const getListCount = (listId?: string): number =>
			(listId && cardIdsByCardListId[listId] && cardIdsByCardListId[listId].length) || 0;
		const boardIssueNo = getListCount(BOARD);
		const backlogIssueNo = getListCount(BACKLOG);
		const sprintStats = cardListIds
			.map((cardListId) => ({
				count: getListCount(cardListId),
				type: getCardListAnalyticsTypeById(cardListId),
			}))
			.filter(({ type }) => type === ACTIVE_SPRINT || type === INACTIVE_SPRINT);
		const activeSprintIssueNo = sprintStats.find(({ type }) => type === ACTIVE_SPRINT)?.count ?? 0;
		const sprintsCount = sprintStats.length;
		const sprintsCardCounts = sprintStats.map(({ count }) => count);
		const sprintsMaxIssueNo = max(sprintsCardCounts);
		const sprintsIssueNo = sum(sprintsCardCounts);
		const sprintsAverageIssueNo = sprintsIssueNo / sprintsCount;

		return {
			boardIssueNo,
			activeSprintIssueNo,
			backlogIssueNo,
			sprintsCount,
			// @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
			sprintsMaxIssueNo,
			sprintsAverageIssueNo,
			sprintsIssueNo,
		};
	},
);

export type ShouldRenderRichElementsProps = {
	cardId: CardId;
};

export const shouldRenderRichElements = createSelector(
	[
		(state: State, props: ShouldRenderRichElementsProps) => props.cardId,
		(state: State) => getRichActiveCard(state),
		(state: State) => getOrderedCardIdsByCardListId(state),
		(state: State) => makeOrderedVisibleCardIdsForCardList(state),
	],
	(targetCardId, activeCardId, orderedCardIdsByCardListId, getVisibleCardsForCardList) => {
		if (!activeCardId) {
			return false;
		}
		if (targetCardId === activeCardId) {
			return true;
		}

		const activeCardList = Object.entries(orderedCardIdsByCardListId).find(
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			([, cardIds]: [any, any]) => Array.isArray(cardIds) && cardIds.includes(activeCardId),
		);
		if (!activeCardList) {
			return false;
		}

		const [activeCardListId] = activeCardList;
		const activeCardListCardIds = getVisibleCardsForCardList(activeCardListId);
		if (!Array.isArray(activeCardListCardIds)) {
			return false;
		}

		const activeCardIndex = activeCardListCardIds.indexOf(activeCardId);
		const targetCardIndex = activeCardListCardIds.indexOf(targetCardId);
		return (
			activeCardIndex !== -1 && targetCardIndex !== -1 && targetCardIndex === activeCardIndex + 1
		);
	},
);

export const makeGetDefaultRowSize = createSelector(
	[hasCardExtraFields],
	(hasExtraFields) =>
		({
			isInlineCardCreateOpen,
			numberOfCardsInCardEntry,
			isCompact,
			hideExtraFields = false,
		}: {
			isInlineCardCreateOpen: boolean;
			numberOfCardsInCardEntry: number;
			isCompact: boolean;
			hideExtraFields?: boolean;
		}) => {
			const cardRowHeight = isCompact ? COMPACT_CARD_ROW_HEIGHT : CARD_ROW_HEIGHT;

			const shouldHideExtraFields = isViewSettingAsPanelExpEnabledWithNoExposure()
				? hideExtraFields
				: false;

			const rowHeight =
				hasExtraFields && !shouldHideExtraFields
					? cardRowHeight + EXTRA_CARD_ROW_HEIGHT
					: cardRowHeight;
			const groupHeight = rowHeight * numberOfCardsInCardEntry;
			const inlineCreateHeight = isInlineCardCreateOpen ? INLINE_CARD_CREATE_ROW_HEIGHT : 0;

			return groupHeight + inlineCreateHeight;
		},
);

export const getDefaultCardHeight = createSelector(
	[hasCardExtraFields],
	(hasExtraFields) => (isCompact?: boolean) => {
		const cardRowHeight = isCompact ? COMPACT_CARD_ROW_HEIGHT : CARD_ROW_HEIGHT;
		return hasExtraFields ? cardRowHeight + EXTRA_CARD_ROW_HEIGHT : cardRowHeight;
	},
);

export const getCardListDragState = (state: State) => state.ui.cardList.dragState;
export const getIsCardListDragInProgress = (state: State, cardListId: string) =>
	Boolean(getCardListDragState(state)[cardListId]);

export const getIsDragging = (state: State) => state.ui.cardList.isDragging;

export const makeGetSourceCardListId = createSelector(
	[getOrderedCardIdsByCardListId],
	(orderedCardListIds) =>
		memoize((cardId) =>
			Object.keys(orderedCardListIds).find((cardListId) =>
				orderedCardListIds[cardListId].includes(cardId),
			),
		),
);

export const makeGetSourceCardGroupId = createSelector(
	[makeOrderedVisibleCardEntries],
	(orderedCardEntriesByCardListId) => {
		const getCardGroupId = (cardListId: string | undefined, cardId: number): string | undefined => {
			if (cardListId === undefined) return undefined;
			const orderedCardEntries = orderedCardEntriesByCardListId(cardListId);
			const group = orderedCardEntries.find(
				(entry) =>
					entry.type === GROUP_TYPE && entry.parentId !== cardId && entry.cardIds.includes(cardId),
			);
			return group !== null && group?.type === GROUP_TYPE
				? `${group.parentId}-${cardListId}`
				: undefined;
		};
		return memoize(getCardGroupId);
	},
);

export const cardVersionsSelector = (state: State, cardId: CardId) => {
	const versions = getEntitiesVersions(state);
	const cards = getCards(state);
	const card = getCardById(cards, cardId);
	return card?.fixVersions?.map((versionId) => versions?.[versionId]).filter(Boolean) ?? [];
};

export const getFirstNonEmptyCardListId = createSelector(
	[getVisibleCardListIds, makeOrderedVisibleCardIdsForCardList, makeIsCardListCollapsed],
	(visibleCardListIds, getOrderedVisibleCardIdsForCardList, getIsCardListCollapsed) =>
		visibleCardListIds.find(
			(listId) =>
				getOrderedVisibleCardIdsForCardList(listId).length > 0 && !getIsCardListCollapsed(listId),
		),
);

export const backlogHasFlaggedCards = createSelector([getCards], (cards) =>
	some(cards, (card) => !!card?.isFlagged),
);

export const backlogHasPriority = createSelector([getCards], (cards) =>
	some(cards, (card) => !!card?.priority),
);

export const backlogHasDueDate = createSelector([getCards], (cards) =>
	some(cards, (card) => !!card?.dueDate),
);

export const backlogHasStoryPoints = createSelector([getCards], (cards) =>
	some(cards, (card) => !!card?.estimate?.storyPoints),
);

export const backlogHasEstimates = createSelector([getCards], (cards) =>
	some(cards, (card) => !!card?.estimate?.originalEstimate?.value),
);

export const getNumberOfCardsWithDueDates = createSelector(
	[getCards],
	(cards) => values(cards).filter((card) => !!card?.dueDate).length,
);

/**
 * Get the listId of the last non-empty card list even if it is collapsed
 */
export const getLastNonEmptyCardListId = createSelector(
	[getOrderedCardListIds, makeOrderedVisibleCardIdsForCardList],
	(orderedCardListIds, getOrderedVisibleCardIdsForCardList) =>
		findLast(
			orderedCardListIds,
			(listId) => getOrderedVisibleCardIdsForCardList(listId).length > 0,
		),
);

/** Given the cardId that is being dragged, return a list of cardIds to include in the drag preview */
export const makeGetDraggingCardIds = createSelector(
	[isCmpKanbanProject, getSelectedCards, getFlattenOrderedCardIds, getParentIdByChildIdSelector],
	(isCmpKanbanBacklog, selectedCardIds, orderedCardIds, getParentIdByChildId) =>
		memoize((draggingCardId: number) => {
			const relevantCards = new Set(
				selectedCardIds.includes(draggingCardId) ? selectedCardIds : [draggingCardId],
			);

			return orderedCardIds.filter((cardId) => {
				if (isCmpKanbanBacklog) return relevantCards.has(cardId);

				const parentId = getParentIdByChildId(cardId);
				return relevantCards.has(cardId) && (!parentId || !relevantCards.has(parentId));
			});
		}),
);

export const getCheckboxSelectionMode = (state: State) => state.ui.cards.isCheckboxSelectionMode;
